import Typography from '@/common/components/base/typography/Typography';
import TextIcon from '@/modules/Education/assets/icons/document-text.svg?react';
import VoiceIcon from '@/modules/Education/assets/icons/microphone.svg?react';
import VideoIcon from '@/modules/Education/assets/icons/video.svg?react';
import { useNavigate } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';
import { EDUCATION_LINKS } from '../EducationLinks';
import { EducationItemType } from '../queries/getEducationList/getEducationListQuery.type';
import { EducationCardSize } from '../types/Education.type';

const typeIconMap = {
  audio: VoiceIcon,
  video: VideoIcon,
  text: TextIcon,
};

type Props = EducationItemType & {
  cardSize?: EducationCardSize;
};

export const formatDuration = (seconds: number | string | null | undefined) => {
  const totalSeconds = Number(seconds ?? 0);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

export default function EducationCard({
  title,
  abstraction,
  content_duration,
  image_cover,
  type,
  id,
  categories = [],
  cardSize = 'normal',
}: Props) {
  const navigate = useNavigate();
  const TypeIcon = typeIconMap[type];
  const visibleCategories = categories?.slice(0, 2) || [];
  const extraCount = Math.max((categories?.length || 0) - 2, 0);

  const isSmall = cardSize === 'small';
  const isMedium = cardSize === 'medium';

  const categoryTextStyle = isSmall ? 'b3' : 'b2';

  return (
    <Box
      width="100%"
      maxWidth={isSmall ? '224px' : isMedium ? '80%' : '100%'}
      minWidth={isSmall ? '224px' : '280px'}
      border="1px solid #EBEBEB"
      borderRadius={16}
      overflow="hidden"
      bgColor="white"
      flexShrink={0}
      className={css({
        transition: 'all 0.2s ease-in-out',
      })}
      onClick={() => navigate(EDUCATION_LINKS.detail(id))}
    >
      <Box position="relative" borderTopRadius={16}>
        <>
          <img
            src={image_cover}
            alt={'education-cover'}
            className={css({
              w: '100%',
              h: isSmall ? '126px' : '180px',
              objectFit: image_cover ? 'cover' : 'contain',
            })}
          />
        </>

        <Flex
          alignItems="center"
          gap={4}
          position="absolute"
          bottom={12}
          left={12}
          bgColor="#00000080"
          px={type === 'text' ? 2 : 4}
          py={2}
          borderRadius={8}
        >
          {type !== 'text' && (
            <Typography color="white" textStyle="b2">
              {formatDuration(content_duration)}
            </Typography>
          )}

          <TypeIcon />
        </Flex>
      </Box>

      <Box p={isSmall ? 12 : 16}>
        <Typography
          textStyle={'b1'}
          color="black"
          fontWeight="bold"
          mb={4}
          className={css({
            lineClamp: 1,
            direction: 'rtl',
          })}
        >
          {title}
        </Typography>

        {!isSmall && (
          <>
            <Typography
              textStyle={isSmall ? 'b3' : 'b2'}
              color="black-1"
              mb={8}
              className={css({
                lineClamp: 1,
                direction: 'rtl',
              })}
            >
              {abstraction}
            </Typography>

            <Flex gap={6} flexWrap="wrap">
              {visibleCategories?.map((category) => (
                <Typography
                  key={category?.id}
                  textStyle={categoryTextStyle}
                  color="black-2"
                  bgColor="gray-2"
                  px={6}
                  py={1}
                  borderRadius={8}
                >
                  {category?.title}
                </Typography>
              ))}

              {extraCount > 0 && (
                <Typography
                  textStyle={categoryTextStyle}
                  color="black-2"
                  bgColor="gray-2"
                  px={6}
                  py={1}
                  borderRadius={8}
                >
                  {extraCount}+ مورد
                </Typography>
              )}
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
}
